import { createContext, useCallback, useContext, useRef } from 'react';
import type { MutableRefObject, RefObject } from 'react';
import { useLatestRef } from '@gonfalon/react/useLatestRef';

import type { FlattenedToken } from '../../../types/QueryBuilder';

import type { GridSelectionState } from './useGridSelection';

export interface KeyboardSelectionContextValue {
  /** Tracks the "moving end" (cursor end) of the current selection. */
  cursorKeyRef: MutableRefObject<string | null>;
  /**
   * Extend or contract the selection by one "filter unit" in the given direction.
   * On the first Shift+Arrow press from a focused token, pass the token's key as
   * `fromKey`. On subsequent presses (while the hidden input has focus), omit it
   * so the cursor ref is used instead.
   */
  selectInDirection: (direction: 'left' | 'right', fromKey?: string) => void;
  /** The grid's selection state — provides selectedKeys, setSelectedKeys, selectAll, etc. */
  state: GridSelectionState;
  /** All rendered token keys in DOM order: content tokens interleaved with FREE_TEXT boundaries. */
  allKeys: string[];
  /** Returns true for keys that represent FREE_TEXT cursor-landing tokens. */
  isFreeTextKey: (key: string) => boolean;
  /** Flat token array used to map selected keys back to their ParsedTokens. */
  flatTokens: FlattenedToken[];
  /** Ref to the hidden SelectionKeyHandler input, used for programmatic focus. */
  hiddenInputRef: RefObject<HTMLInputElement | null>;
}

const KeyboardSelectionContext = createContext<KeyboardSelectionContextValue | null>(null);

export function useKeyboardSelectionContext(): KeyboardSelectionContextValue | null {
  return useContext(KeyboardSelectionContext);
}

export { KeyboardSelectionContext };

/**
 * Creates the value for KeyboardSelectionContext.
 * Must be called inside FilterBar (render-prop mode only).
 */
export function useKeyboardSelection({
  state,
  allKeys,
  isFreeTextKey,
  flatTokens,
  hiddenInputRef,
}: {
  state: GridSelectionState;
  allKeys: string[];
  isFreeTextKey: (key: string) => boolean;
  flatTokens: FlattenedToken[];
  hiddenInputRef: RefObject<HTMLInputElement | null>;
}): KeyboardSelectionContextValue {
  const cursorKeyRef = useRef<string | null>(null);

  // Stable refs so selectInDirection doesn't change identity on every render.
  const allKeysRef = useLatestRef(allKeys);
  const isFreeTextKeyRef = useLatestRef(isFreeTextKey);
  const stateRef = useLatestRef(state);

  const selectInDirection = useCallback(
    (direction: 'left' | 'right', fromKey?: string) => {
      const keys = allKeysRef.current;
      const isFT = isFreeTextKeyRef.current;
      const sm = stateRef.current;

      const cursorKey = fromKey ?? cursorKeyRef.current;
      if (!cursorKey) {
        return;
      }

      const cursorIndex = keys.indexOf(cursorKey);
      if (cursorIndex === -1) {
        return;
      }

      const step = direction === 'right' ? 1 : -1;

      // If the cursor is already parked on a FREE_TEXT boundary, step one further
      // in the direction to avoid redundantly re-selecting that boundary.
      let startIndex: number;
      if (isFT(cursorKey)) {
        startIndex = cursorIndex + step;
      } else {
        startIndex = cursorIndex;
      }

      if (startIndex < 0 || startIndex >= keys.length) {
        return;
      }

      // Walk from startIndex in the direction until a FREE_TEXT token is reached.
      // That FREE_TEXT becomes the endKey (the new cursor position).
      let endIndex = startIndex;
      while (!isFT(keys[endIndex])) {
        const next = endIndex + step;
        if (next < 0 || next >= keys.length) {
          break;
        }
        endIndex = next;
      }

      const endKey = keys[endIndex];

      // XOR-merge the inclusive range [lo..hi] with the existing selection.
      // Keys already selected are deselected; keys not yet selected are added.
      // This means pressing Shift+Arrow back toward the anchor shrinks the selection.
      const lo = Math.min(startIndex, endIndex);
      const hi = Math.max(startIndex, endIndex);
      const rangeKeys = keys.slice(lo, hi + 1);

      const current = new Set<string>(sm.selectedKeys);
      for (const key of rangeKeys) {
        if (current.has(key)) {
          current.delete(key);
        } else {
          current.add(key);
        }
      }

      sm.setSelectedKeys(current);
      cursorKeyRef.current = endKey;
    },
    // No deps — all mutable state is accessed through stable refs.
    [],
  );

  return {
    cursorKeyRef,
    selectInDirection,
    state,
    allKeys,
    isFreeTextKey,
    flatTokens,
    hiddenInputRef,
  };
}
